import { useAsyncData } from '#imports'

import type { Review as ApiReview } from '@backmarket/http-api/src/api-specs-reviews/types/review'
import { mapToReviews } from '@backmarket/nuxt-layer-cms/Reviews.utils'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'

import type { ReviewWithCount } from '~/scopes/product-core/components/presentationals/GalleryNew/GalleryNew.types'
import { useFetchProduct } from '~/scopes/product-core/composables/useFetch/useFetchProduct/useFetchProduct'
import { getOptionalEndpointTimeoutOptions } from '~/scopes/product-core/composables/useFetch/utils/getOptionalEndpointTimeoutOptions'
import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'

import {
  BLOCKED_REVIEW_IDS,
  FIVE_STAR,
  MAX_AGE_MONTHS,
  MAX_REVIEWS,
  MIN_RATING,
  MIN_VOTES,
} from './useFetchAutomatedReviewsForCarousel.constants'

const MAX_AGE_DATE = new Date()
MAX_AGE_DATE.setMonth(MAX_AGE_DATE.getMonth() - MAX_AGE_MONTHS)

/**
 * Client-side filters when API does not support: at least 1 vote, not older than 12 months.
 * mapToReviews already keeps only reviews with pictures.
 */
function filterApiReviews(apiReviews: ApiReview[]): ApiReview[] {
  return apiReviews.filter((r) => {
    // Blocked reviews are never displayed.
    if (BLOCKED_REVIEW_IDS.has(r.id)) return false

    // Reviews not older than 12 months are not displayed.
    // Redundant filter, but kept for clarity.
    const createdAt = r.createdAt ? new Date(r.createdAt) : null
    if (createdAt && createdAt < MAX_AGE_DATE) return false

    // Reviews with less than 1 vote are not displayed.
    const votes = r.voteCount ?? 0
    if (votes < MIN_VOTES) return false

    // Reviews with less than 4.5 stars are not displayed.
    if (r.averageRate < MIN_RATING) return false

    return true
  })
}

/**
 * Orders reviews for carousel: [small1, small2, big] with 5-star as big when available.
 */
function buildCarouselReviews(apiReviews: ApiReview[]): ReviewWithCount[] {
  const filtered = filterApiReviews(apiReviews)
  const withPictures = mapToReviews(filtered)
  const totalCount = withPictures.length

  const fiveStar = withPictures.filter((r) => r.averageRate >= FIVE_STAR)
  const fourPointFiveToFive = withPictures.filter(
    (r) => r.averageRate >= MIN_RATING && r.averageRate < FIVE_STAR,
  )

  const result: ReviewWithCount[] = []

  // Take up to 2 small reviews (4.5–4.9 stars)
  const takeSmall = Math.min(2, fourPointFiveToFive.length)
  for (let i = 0; i < takeSmall; i += 1) {
    result.push({ ...fourPointFiveToFive[i], count: totalCount })
  }

  // Fill remaining slots with 5-star reviews
  for (const r of fiveStar) {
    if (result.length >= MAX_REVIEWS) break
    result.push({ ...r, count: totalCount })
  }

  return result
}

export async function fetchAutomatedProductReviewsForCarousel({
  uuid,
}: {
  uuid: string
}): Promise<ReviewWithCount[] | null> {
  const $httpFetch = useHttpFetch()
  const experiments = useExperiments()
  const locale = useI18nLocale()

  const isAutomatedReviewsEnabled =
    experiments['experiment.ppAutomatedReviewsInCarousel'] ===
    'withAutomatedReviews'

  if (!isAutomatedReviewsEnabled) {
    return null
  }

  const data = await $httpFetch('/reviews/v1/products/:uuid/reviews', {
    pathParams: { uuid },
    query: {
      order_by: '-relevance',
      created_at__gte: MAX_AGE_DATE.toISOString().split('T')[0],
      created_at__lte: new Date().toISOString().split('T')[0],
      global_rate__gte: MIN_RATING,
      page_size: 20,
      with_attachments: true,
      translation_locale: locale,
    },
  })

  if (!data?.results?.length) {
    if (import.meta.dev) {
      const { MOCK_REVIEWS_RESPONSE } =
        await import('./useFetchAutomatedReviewsForCarousel.mock')

      return buildCarouselReviews(MOCK_REVIEWS_RESPONSE.results)
    }

    return null
  }

  return buildCarouselReviews(data.results as ApiReview[])
}

export function useFetchAutomatedReviewsForCarousel() {
  const experiments = useExperiments()
  const { productId } = useUrlParams()
  const { data: productResponse } = useFetchProduct()

  const isAutomatedReviewsEnabled =
    experiments['experiment.ppAutomatedReviewsInCarousel'] ===
    'withAutomatedReviews'

  return useAsyncData(
    `pp-automated-product-reviews-for-carousel-${productId}`,
    async function fetchProductReviewsForCarousel() {
      const uuid = productResponse.value?.productId ?? productId
      if (!isAutomatedReviewsEnabled || !uuid) {
        return null
      }

      return fetchAutomatedProductReviewsForCarousel({ uuid })
    },
    {
      dedupe: 'defer',
      ...getOptionalEndpointTimeoutOptions(),
    },
  )
}
